import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppTheme from "../../../theme/AppTheme";
import { dataGridCustomizations } from "../../../theme/customizations";
import {
  Alert,
  Box,
  Button,
  Chip,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  notifyError,
  notifySuccess,
} from "../../../components/ToastContainer/Toast/Toast";
import { useAppSelector } from "../../../Redux/hooks";
import TopupSettingsCard from "./TopupSettingsCard";
import { giveCompensation } from "../../../service/BuyerConfiguration/Promotions/promotionService";
import {
  adjustWallet,
  getUserTopups,
  getWalletDetail,
  getWalletSummary,
  newIdempotencyKey,
  searchWallets,
  setWalletStatus,
  WalletAdminBucket,
  WalletDetail,
  WalletListItem,
  WalletSummary,
  WalletTransactionRow,
  TopupRow,
} from "../../../service/BuyerConfiguration/Wallets/walletService";

/**
 * Tazty Wallets.
 *
 * Find a customer by phone or name, see their balance by bucket and full
 * history, and credit, debit or freeze the wallet. Every change needs a
 * reason and is kept in the ledger with who made it. Customers only see the
 * wallet once WALLET_ENABLED is on (or their id is in WALLET_TEST_USER_IDS),
 * but credits made here are real balance either way.
 */

const inr = (n: number | null | undefined) =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const BUCKET_LABEL: Record<string, string> = {
  refund: "Refund",
  topup: "Added money",
  promo: "Promo",
  cashback: "Cashback",
};

const PAGE_SIZE = 20;

type AdjustState = {
  direction: "credit" | "debit";
  amount: string;
  bucket: WalletAdminBucket;
  reason: string;
  customer_note: string;
  notify: boolean;
  idempotency_key: string;
};

const Wallets = (props: { disableCustomTheme?: boolean }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const xThemeComponents = { ...dataGridCustomizations };
  const adminEmail = useAppSelector((state) => state.roleAndPermission.email);

  const [summary, setSummary] = useState<WalletSummary | null>(null);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<WalletListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [detailUserId, setDetailUserId] = useState<number | null>(null);
  const [detail, setDetail] = useState<WalletDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [txnPage, setTxnPage] = useState(0);

  const [adjust, setAdjust] = useState<AdjustState | null>(null);
  const [saving, setSaving] = useState(false);
  const [freezeReason, setFreezeReason] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await getWalletSummary());
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Failed to load wallet totals");
    }
  }, []);

  const loadList = useCallback(async (term: string) => {
    setLoading(true);
    try {
      const res = await searchWallets({ search: term || undefined, limit: 50 });
      setRows(res?.items ?? []);
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Failed to load wallets");
    } finally {
      setLoading(false);
    }
  }, []);

  const [topups, setTopups] = useState<TopupRow[]>([]);

  const loadDetail = useCallback(async (userId: number, page: number) => {
    setDetailLoading(true);
    try {
      setDetail(await getWalletDetail(userId, { page: page + 1, limit: PAGE_SIZE }));
      // Top-ups for support: created / credited / failed, with the fee paid.
      getUserTopups(userId)
        .then((rows) => setTopups(Array.isArray(rows) ? rows : []))
        .catch(() => setTopups([]));
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Failed to load the wallet");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Debounced search: phone digits or a name.
  useEffect(() => {
    const t = setTimeout(() => loadList(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search, loadList]);

  useEffect(() => {
    if (detailUserId) loadDetail(detailUserId, txnPage);
  }, [detailUserId, txnPage, loadDetail]);

  const openDetail = (userId: number) => {
    setDetail(null);
    setTxnPage(0);
    setDetailUserId(userId);
  };

  // Opened from the Wallet Audit Log (/wallets?user=<id>): show that wallet.
  useEffect(() => {
    const userId = Number(searchParams.get("user"));
    if (Number.isInteger(userId) && userId > 0) openDetail(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const closeDetail = () => {
    setDetailUserId(null);
    setDetail(null);
  };

  const refreshAfterChange = async () => {
    if (detailUserId) await loadDetail(detailUserId, 0);
    setTxnPage(0);
    loadSummary();
    loadList(search.trim());
  };

  const [goodwill, setGoodwill] = useState<{
    amount: string;
    reason: string;
    order_id: string;
    expires_in_days: string;
    idempotency_key: string;
  } | null>(null);

  const goodwillAmount = Number(goodwill?.amount || 0);
  const goodwillError = (() => {
    if (!goodwill?.amount) return null;
    if (!Number.isFinite(goodwillAmount) || goodwillAmount < 1) return "Minimum ₹1";
    if (goodwillAmount > 5000) return "Maximum ₹5,000 of goodwill credit";
    return null;
  })();

  const saveGoodwill = async () => {
    if (!goodwill || !detailUserId || goodwillError || goodwill.reason.trim().length < 3) return;
    setSaving(true);
    try {
      const res = await giveCompensation({
        user_id: detailUserId,
        amount: goodwillAmount,
        reason: goodwill.reason.trim(),
        order_id: goodwill.order_id ? Number(goodwill.order_id) : undefined,
        expires_in_days: goodwill.expires_in_days ? Number(goodwill.expires_in_days) : undefined,
        idempotency_key: goodwill.idempotency_key,
      });
      notifySuccess(`${inr(goodwillAmount)} credited - valid ${res.expires_in_days} days`);
      setGoodwill(null);
      await refreshAfterChange();
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Could not give the credit");
    } finally {
      setSaving(false);
    }
  };

  const openAdjust = (direction: "credit" | "debit") =>
    setAdjust({
      direction,
      amount: "",
      bucket: "refund",
      reason: "",
      customer_note: "",
      notify: true,
      // Made once per dialog: a double click or a retry sends the same key.
      idempotency_key: newIdempotencyKey(),
    });

  const adjustAmount = Number(adjust?.amount || 0);
  const adjustError = useMemo(() => {
    if (!adjust) return null;
    if (!adjust.amount) return null;
    if (!Number.isFinite(adjustAmount) || adjustAmount < 1) return "Minimum ₹1";
    if (adjustAmount > 10000) return "Maximum ₹10,000 per entry";
    if (!/^\d+(\.\d{1,2})?$/.test(adjust.amount)) return "Up to 2 decimals";
    if (adjust.direction === "debit" && detail && adjustAmount > detail.buckets[adjust.bucket])
      return `Only ${inr(detail.buckets[adjust.bucket])} in ${BUCKET_LABEL[adjust.bucket]}`;
    return null;
  }, [adjust, adjustAmount, detail]);

  const canSaveAdjust =
    !!adjust && !!adjust.amount && !adjustError && adjust.reason.trim().length >= 3 && !saving;

  const saveAdjust = async () => {
    if (!adjust || !detailUserId || !canSaveAdjust) return;
    setSaving(true);
    try {
      const res: any = await adjustWallet(detailUserId, adjust.direction, {
        amount: adjustAmount,
        bucket: adjust.bucket,
        reason: adjust.reason.trim(),
        customer_note:
          adjust.direction === "credit" && adjust.customer_note.trim()
            ? adjust.customer_note.trim()
            : undefined,
        idempotency_key: adjust.idempotency_key,
        created_by: adminEmail || undefined,
        notify: adjust.direction === "credit" ? adjust.notify : false,
      });
      notifySuccess(
        res?.duplicate
          ? "Already saved — nothing was added twice"
          : adjust.direction === "credit"
            ? `${inr(adjustAmount)} added to the wallet`
            : `${inr(adjustAmount)} taken from the wallet`,
      );
      setAdjust(null);
      await refreshAfterChange();
    } catch (e: any) {
      // The dialog stays open with the same key, so "Save" again is safe.
      notifyError(e?.response?.data?.message || "Could not update the wallet");
    } finally {
      setSaving(false);
    }
  };

  const saveStatus = async (status: "active" | "frozen", reason?: string) => {
    if (!detailUserId) return;
    setSaving(true);
    try {
      await setWalletStatus(detailUserId, status, reason);
      notifySuccess(status === "frozen" ? "Wallet frozen" : "Wallet unfrozen");
      setFreezeReason(null);
      await refreshAfterChange();
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Could not change the wallet status");
    } finally {
      setSaving(false);
    }
  };

  const listColumns: GridColDef<WalletListItem>[] = [
    { field: "name", headerName: "Customer", flex: 1, minWidth: 160, valueGetter: (_v, r) => r.name || "—" },
    { field: "phone_number", headerName: "Phone", width: 140 },
    {
      field: "balance",
      headerName: "Balance",
      width: 130,
      renderCell: (p) => <b>{p.row.has_wallet ? inr(p.row.balance) : "No wallet yet"}</b>,
    },
    { field: "refund", headerName: "Refund", width: 110, valueGetter: (_v, r) => inr(r.buckets?.refund) },
    { field: "promo", headerName: "Promo", width: 110, valueGetter: (_v, r) => inr(r.buckets?.promo) },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (p) =>
        p.row.has_wallet ? (
          <Chip
            size="small"
            label={p.row.status === "frozen" ? "Frozen" : "Active"}
            color={p.row.status === "frozen" ? "error" : "success"}
            variant="outlined"
          />
        ) : null,
    },
    {
      field: "actions",
      headerName: "",
      width: 110,
      sortable: false,
      renderCell: (p) => (
        <Button size="small" onClick={() => openDetail(p.row.user_id)}>
          Open
        </Button>
      ),
    },
  ];

  const txnColumns: GridColDef<WalletTransactionRow>[] = [
    {
      field: "created_at",
      headerName: "When",
      width: 160,
      valueGetter: (_v, r) => new Date(r.created_at).toLocaleString("en-IN"),
    },
    { field: "title", headerName: "Type", width: 170 },
    {
      field: "amount",
      headerName: "Amount",
      width: 120,
      renderCell: (p) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: p.row.direction === "credit" ? "success.main" : "error.main" }}
        >
          {p.row.direction === "credit" ? "+" : "−"}
          {inr(p.row.amount)}
        </Typography>
      ),
    },
    { field: "bucket", headerName: "Bucket", width: 110, valueGetter: (_v, r) => BUCKET_LABEL[r.bucket] || r.bucket },
    { field: "balance_after", headerName: "Balance after", width: 130, valueGetter: (_v, r) => inr(r.balance_after) },
    { field: "reason", headerName: "Reason", flex: 1, minWidth: 200 },
    { field: "created_by", headerName: "By", width: 170 },
    { field: "txn_ref", headerName: "Reference", width: 190 },
  ];

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" }, p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2, alignItems: { sm: "center" }, justifyContent: "space-between" }}
        >
          <Box>
            <Typography variant="h5">Tazty Wallets</Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Wallet balance can only be spent on Tazty. Every change needs a reason and is kept
              in the history.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="outlined" onClick={() => navigate("/wallets/audit-log")}>
              Audit log
            </Button>
            <TextField
              size="small"
              placeholder="Search by phone or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 260 }}
            />
          </Stack>
        </Stack>

        <TopupSettingsCard />

        {summary && (
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Wallet in the buyer app
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  label={summary.enabled ? "ON for all customers" : "OFF (test users only)"}
                  color={summary.enabled ? "success" : "default"}
                />
              </Box>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Total Tazty owes customers
              </Typography>
              <Typography variant="h6">{inr(summary.total)}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Refund {inr(summary.buckets.refund)} · Promo {inr(summary.buckets.promo)} · Added{" "}
                {inr(summary.buckets.topup)} · Cashback {inr(summary.buckets.cashback)}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Wallets
              </Typography>
              <Typography variant="h6">{summary.accounts}</Typography>
              <Typography variant="caption" color={summary.frozen ? "error" : undefined}>
                {summary.frozen} frozen
              </Typography>
            </Paper>
          </Stack>
        )}

        <DataGrid
          rows={rows}
          columns={listColumns}
          loading={loading}
          getRowId={(r) => r.user_id}
          disableRowSelectionOnClick
          autoHeight
          pageSizeOptions={[50]}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          localeText={{
            noRowsLabel: search ? "No customer matches this search" : "No wallets yet — search a customer to credit one",
          }}
        />
      </Box>

      {/* Wallet detail */}
      <Dialog open={!!detailUserId} onClose={closeDetail} maxWidth="lg" fullWidth>
        <DialogTitle>
          {detail?.user?.name || "Customer"}{" "}
          <Typography component="span" sx={{ opacity: 0.7 }}>
            {detail?.user?.phone_number ? `· ${detail.user.phone_number}` : ""}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <>
              {detail.status === "frozen" && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Frozen — it can receive money but nothing can be spent.{" "}
                  {detail.frozen_reason ? `Reason: ${detail.frozen_reason}` : ""}
                </Alert>
              )}
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, minWidth: 200 }}>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Balance
                  </Typography>
                  <Typography variant="h5">{inr(detail.balance)}</Typography>
                </Paper>
                {(["refund", "promo", "topup", "cashback"] as const).map((b) => (
                  <Paper key={b} variant="outlined" sx={{ p: 2, flex: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {BUCKET_LABEL[b]}
                    </Typography>
                    <Typography variant="subtitle1">{inr(detail.buckets[b])}</Typography>
                  </Paper>
                ))}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button variant="contained" onClick={() => openAdjust("credit")}>
                  Add money
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={detail.balance <= 0}
                  onClick={() => openAdjust("debit")}
                >
                  Deduct
                </Button>
                <Button
                  variant="outlined"
                  onClick={() =>
                    setGoodwill({
                      amount: "",
                      reason: "",
                      order_id: "",
                      expires_in_days: "7",
                      // Made once per dialog: a double click sends the same key
                      // and the server credits only once.
                      idempotency_key: newIdempotencyKey(),
                    })
                  }
                >
                  Goodwill credit
                </Button>
                <Box sx={{ flex: 1 }} />
                {detail.status === "frozen" ? (
                  <Button color="success" disabled={saving} onClick={() => saveStatus("active")}>
                    Unfreeze
                  </Button>
                ) : (
                  <Button color="error" onClick={() => setFreezeReason("")}>
                    Freeze
                  </Button>
                )}
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <DataGrid
                rows={detail.transactions}
                columns={txnColumns}
                loading={detailLoading}
                getRowId={(r) => r.id}
                disableRowSelectionOnClick
                autoHeight
                paginationMode="server"
                rowCount={detail.pagination.total}
                pageSizeOptions={[PAGE_SIZE]}
                paginationModel={{ page: txnPage, pageSize: PAGE_SIZE }}
                onPaginationModelChange={(m) => setTxnPage(m.page)}
                localeText={{ noRowsLabel: "No wallet activity yet" }}
              />
              {topups.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Top-ups
                  </Typography>
                  {topups.map((t) => (
                    <Stack
                      key={t.id}
                      direction="row"
                      spacing={2}
                      sx={{ py: 0.5, borderBottom: "1px solid", borderColor: "divider" }}
                    >
                      <Typography variant="body2" sx={{ width: 150 }}>
                        {new Date(t.created_at).toLocaleString("en-IN")}
                      </Typography>
                      <Typography variant="body2" sx={{ width: 110, fontWeight: 600 }}>
                        {inr(t.amount)}
                      </Typography>
                      <Typography variant="body2" sx={{ width: 170, opacity: 0.8 }}>
                        {t.fee_amount > 0 ? `+ ${inr(t.fee_amount)} fee (customer)` : "fee paid by Tazty"}
                      </Typography>
                      <Chip
                        size="small"
                        label={t.status}
                        color={t.status === "credited" ? "success" : t.status === "failed" ? "error" : "default"}
                      />
                      <Typography variant="caption" sx={{ opacity: 0.7, flex: 1 }}>
                        {t.failure_reason || t.razorpay_payment_id || t.razorpay_order_id}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Credit / debit */}
      <Dialog
        open={!!goodwill}
        onClose={() => !saving && setGoodwill(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Goodwill credit</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Promotional credit that expires and can only be spent on Tazty - use it to say sorry
              for a late or poor order. To return money the customer actually paid, use Add money
              instead.
            </Alert>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Amount"
                size="small"
                value={goodwill?.amount ?? ""}
                onChange={(e) =>
                  setGoodwill((g) => (g ? { ...g, amount: e.target.value } : g))
                }
                error={!!goodwillError}
                helperText={goodwillError}
              />
              <TextField
                label="Valid for (days)"
                size="small"
                type="number"
                value={goodwill?.expires_in_days ?? "7"}
                onChange={(e) =>
                  setGoodwill((g) => (g ? { ...g, expires_in_days: e.target.value } : g))
                }
              />
              <TextField
                label="Order id (optional)"
                size="small"
                value={goodwill?.order_id ?? ""}
                onChange={(e) =>
                  setGoodwill((g) => (g ? { ...g, order_id: e.target.value } : g))
                }
                helperText="Stops the same order being paid twice"
              />
            </Stack>
            <TextField
              label="Reason (shown to the customer)"
              size="small"
              value={goodwill?.reason ?? ""}
              onChange={(e) => setGoodwill((g) => (g ? { ...g, reason: e.target.value } : g))}
              placeholder="Late delivery"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoodwill(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveGoodwill}
            disabled={
              saving ||
              !goodwill?.amount ||
              !!goodwillError ||
              (goodwill?.reason.trim().length ?? 0) < 3
            }
          >
            Give credit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!adjust} onClose={() => !saving && setAdjust(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{adjust?.direction === "credit" ? "Add money to wallet" : "Deduct from wallet"}</DialogTitle>
        <DialogContent dividers>
          {adjust && (
            <Stack spacing={2}>
              <TextField
                label="Amount (₹)"
                value={adjust.amount}
                onChange={(e) => setAdjust({ ...adjust, amount: e.target.value.replace(/[^\d.]/g, "") })}
                error={!!adjustError}
                helperText={adjustError || "₹1 – ₹10,000"}
                autoFocus
              />
              <Alert severity="info">
                This moves <strong>refund balance</strong>, which never expires and is real
                money we owe the customer. To give promotional credit that expires, use
                Goodwill credit instead.
              </Alert>
              <TextField
                label="Reason (internal, required)"
                value={adjust.reason}
                onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })}
                placeholder="e.g. Goodwill for late delivery on order #1234"
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
              />
              {adjust.direction === "credit" && (
                <>
                  <TextField
                    label="Message to customer (optional)"
                    value={adjust.customer_note}
                    onChange={(e) => setAdjust({ ...adjust, customer_note: e.target.value })}
                    placeholder="Sorry for the delay on your last order."
                    inputProps={{ maxLength: 200 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={adjust.notify}
                        onChange={(e) => setAdjust({ ...adjust, notify: e.target.checked })}
                      />
                    }
                    label="Send a push notification (only if the customer can see the wallet)"
                  />
                </>
              )}
              {adjust.amount && !adjustError && (
                <Alert severity={adjust.direction === "credit" ? "info" : "warning"}>
                  {adjust.direction === "credit" ? "Adding" : "Deducting"} <b>{inr(adjustAmount)}</b>{" "}
                  {adjust.direction === "credit" ? "to" : "from"} {detail?.user?.name || "this customer"}'s{" "}
                  {BUCKET_LABEL[adjust.bucket]} balance.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjust(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={adjust?.direction === "debit" ? "warning" : "primary"}
            disabled={!canSaveAdjust}
            onClick={saveAdjust}
          >
            {saving ? "Saving…" : adjust?.direction === "credit" ? "Add money" : "Deduct"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Freeze */}
      <Dialog open={freezeReason !== null} onClose={() => setFreezeReason(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Freeze wallet</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The customer keeps their balance and can still receive refunds, but cannot spend until
            you unfreeze it.
          </Typography>
          <TextField
            fullWidth
            label="Reason"
            value={freezeReason ?? ""}
            onChange={(e) => setFreezeReason(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFreezeReason(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={saving || !(freezeReason || "").trim()}
            onClick={() => saveStatus("frozen", (freezeReason || "").trim())}
          >
            Freeze
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
};

export default Wallets;
